import { Flex, Modal, notification } from "antd";
import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiError, postSchedulerAddUnscheduledVisits } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Market } from "tm-shared/types";
import { countText } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import { MarketCard } from "@/components/MarketCard";
import { PrimaryText } from "@/components/Typography/Styled";
import { ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { StyledButton } from "@/pages/HR/AddUser/Styled";
import { setUpdateUnscheduledMarketsProcessing } from "@/store/slices/offline";

import { StyledMarketCard } from "./Styled";

interface AddMarketConfirmProps {
    modalKey: string;
    open: boolean;
    marketExtraIds: string[];
    selectedMarkets: Market[];
    setOpenAddMarketModal: (open: boolean) => void;
}

const AddMarketConfirmModal: React.FC<AddMarketConfirmProps> = ({
    modalKey,
    open,
    marketExtraIds,
    selectedMarkets,
    setOpenAddMarketModal,
}) => {
    const { role, id: userId } = useAppSelector((s) => s.user);
    const { activeSchedule } = useAppSelector((s) => s.sv);
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const availableMarkets = selectedMarkets.filter(
        (market) => !marketExtraIds?.includes(market.id),
    );

    const handleAddMarkets = useCallback(async () => {
        setLoading(true);
        try {
            const marketIds = availableMarkets.map((market) => market.id);

            await postSchedulerAddUnscheduledVisits(
                {
                    agentId: isManagerRole(role) ? activeSchedule : undefined,
                    marketIds,
                    date: dayjs().format(backendDateFormat.date),
                },
                { config: { isAsync: { userId: userId } } },
            );

            dispatch(setUpdateUnscheduledMarketsProcessing(true));
            notification.success({
                message: `Запит на додавання ${countText.unplanned(
                    availableMarkets.length,
                )} ТТ успішно надіслано. Ми повідомимо вас про результат.`,
            });

            navigate(ROUTES.WorkPlan);

            setOpenAddMarketModal(false);
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                message: "Помилка при додаванні магазинів",
                description: displayMessage,
            });
        } finally {
            setLoading(false);
        }
    }, [dispatch, availableMarkets, setOpenAddMarketModal]);

    if (availableMarkets.length === 0) return null;

    return (
        <Modal
            key={modalKey}
            open={open}
            centered
            footer={null}
            onCancel={() => setOpenAddMarketModal(false)}
            width={600}
        >
            <PrimaryText
                fontSize={16}
                lineHeight="20px"
                fontWeight="400"
                padding="0 0 16px 0"
                textAlign="center"
            >
                Буде додано {availableMarkets.length} торгових точок
            </PrimaryText>

            <div
                style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    paddingRight: "8px",
                }}
            >
                <Flex vertical>
                    {availableMarkets.map((market) => (
                        <StyledMarketCard key={market.id}>
                            <MarketCard
                                address={market.address.name}
                                fop={market.contragent.name}
                            />
                        </StyledMarketCard>
                    ))}
                </Flex>
            </div>

            <StyledButton
                type="primary"
                disabled={loading}
                loading={loading}
                onClick={handleAddMarkets}
                style={{
                    backgroundColor: loading ? "gray" : "#49AA19",
                    marginTop: 16,
                    color: "#fff",
                }}
            >
                Додати всі
            </StyledButton>
            <BaseButton
                type="default"
                onClick={() => setOpenAddMarketModal(false)}
                marginTop="16px"
            >
                Скасувати
            </BaseButton>
        </Modal>
    );
};

export default AddMarketConfirmModal;
